import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface Options<K, V> {
  key: K | null;
  value: V | null;
  delay?: number;
  save: (key: K, value: V) => Promise<void>;
  onError?: (err: unknown) => void;
}

interface AutoSaveApi {
  status: SaveStatus;
  flush: () => Promise<void>;
}

const SAVED_LINGER_MS = 1500;
const ERROR_LINGER_MS = 3000;

export function useAutoSave<K, V>({
  key,
  value,
  delay = 500,
  save,
  onError,
}: Options<K, V>): AutoSaveApi {
  const [status, setStatus] = useState<SaveStatus>('idle');

  const saveRef = useRef(save);
  const onErrorRef = useRef(onError);
  saveRef.current = save;
  onErrorRef.current = onError;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lingerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ key: K; value: V } | null>(null);
  const lastKeyRef = useRef<K | null>(null);
  const baselineRef = useRef<V | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearLinger = () => {
    if (lingerRef.current) {
      clearTimeout(lingerRef.current);
      lingerRef.current = null;
    }
  };

  const runSave = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    clearTimer();
    clearLinger();
    setStatus('saving');
    try {
      await saveRef.current(pending.key, pending.value);
      setStatus('saved');
      lingerRef.current = setTimeout(() => setStatus('idle'), SAVED_LINGER_MS);
    } catch (err) {
      onErrorRef.current?.(err);
      setStatus('error');
      lingerRef.current = setTimeout(() => setStatus('idle'), ERROR_LINGER_MS);
    }
  }, []);

  const flush = useCallback(async () => {
    if (!pendingRef.current) return;
    await runSave();
  }, [runSave]);

  useEffect(() => {
    if (key !== lastKeyRef.current) {
      if (pendingRef.current) {
        void runSave();
      }
      lastKeyRef.current = key;
      baselineRef.current = value;
      return;
    }

    if (key === null || value === null) return;
    if (Object.is(value, baselineRef.current)) return;

    pendingRef.current = { key, value };
    setStatus('saving');
    clearTimer();
    timerRef.current = setTimeout(() => {
      baselineRef.current = value;
      void runSave();
    }, delay);
  }, [key, value, delay, runSave]);

  useEffect(() => {
    return () => {
      clearTimer();
      clearLinger();
      if (pendingRef.current) {
        const { key: k, value: v } = pendingRef.current;
        pendingRef.current = null;
        void saveRef.current(k, v).catch((err) => onErrorRef.current?.(err));
      }
    };
  }, []);

  return { status, flush };
}
